import React from 'react'
import { toast } from 'react-toastify'
import styled from '@emotion/styled'
import ChangeCircleIcon from '@mui/icons-material/ChangeCircle'
import SendIcon from '@mui/icons-material/Send'
import { LoadingButton } from '@mui/lab'
import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  TextField
} from '@mui/material'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import EsimAPI from 'api/faber/EsimAPI'
import ReplacementAPI from 'api/faber/ReplacementAPI'
import SimOpsAPI, { getSimOpsErrorMessage } from 'api/ops/v2/SimOpsAPI'
import DemoDatePicker from 'common/AbstractSingleDatePicker'
import CrossDialogBtn from 'common/Button/CrossDialogBtn'
import NormalText from 'common/Typography/NormalText'
import TextColorFontBody from 'common/Typography/TextColorFontBody'
import circlesOrbitColorPalette from 'common/ui/circlesColorTheme'
import OrbitSession from 'hooks/useCookies'
import { PaperComponent } from 'pages/Customers/sharedDialogs'

import {
  refetchUserAllocations,
  refetchUserEsimQueries
} from '../simOpsQueries'

const StyledIcon = styled(Box)({
  backgroundColor: circlesOrbitColorPalette.toolkit.main,
  height: '10vh',
  width: '10vh',
  borderRadius: '50%',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  boxShadow: 'rgba(0, 0, 0, 0.1) 0px 10px 50px',
  border: 'none'
})

function EsimRequestV3Dialog({
  handleRequestEsimDialogClose,
  requestEsimDialog,
  userId,
  esimICCID,
  faberToken,
  isExternal
}: RequestDialogProps) {
  const queryClient = useQueryClient()
  const userEmail = OrbitSession.get('email')

  const { mutate: replaceEsim, isLoading: isReplaceEsimLoading } = useMutation(
    SimOpsAPI.replaceEsim,
    {
      onSuccess: (res) => {
        const { esim, resetPacks, failures } = res.data?.data ?? {}
        toast.success(
          `eSIM replaced with ${esim?.iccid ?? 'new eSIM'}, ${
            resetPacks?.length ?? 0
          } pack(s) moved over`
        )
        if (failures?.length) {
          toast.error(
            `${failures.length} pack(s) failed to reset: ${failures
              .map((f) => f.packAssignmentId)
              .join(', ')}`
          )
        }
        refetchUserEsimQueries(queryClient, userId)
        refetchUserAllocations(queryClient, userId)
      },
      onError: (error) => {
        toast.error(getSimOpsErrorMessage(error, 'Error replacing eSIM'))
      },
      onSettled: () => {
        handleRequestEsimDialogClose()
      }
    }
  )

  // the ops replace endpoint is internal only, external users keep the old flow
  const {
    mutate: replaceExternalEsim,
    isLoading: isReplaceExternalEsimLoading
  } = useMutation(ReplacementAPI.externalEsimOTPReplacement, {
    onSuccess: () => {
      toast.success('New eSim has been requested for selected customer')
    },
    onError: () => {
      toast.error('Error requesting for new esim')
    },
    onSettled: () => {
      handleRequestEsimDialogClose()
    }
  })

  const { mutate: resendEsimEmail, isLoading: isEmailSendingLoading } =
    useMutation(
      isExternal ? EsimAPI.externalResendEsimEmail : EsimAPI.resendEsimEmail,
      {
        onSuccess: () => {
          toast.success('Resent esim subscription email')
        },
        onError: () => {
          toast.error('Error resending email')
        },
        onSettled: () => {
          handleRequestEsimDialogClose()
        }
      }
    )
  const handleConfirmRequest = (actionEvent: string) => {
    if (actionEvent === 'replace') {
      if (isExternal) {
        replaceExternalEsim({
          userId: userId,
          remarks: 'replaced from ops dashboard',
          faberToken: faberToken,
          oldICCID: esimICCID
        })
      } else {
        replaceEsim({ iccid: esimICCID })
      }
    } else
      resendEsimEmail({
        userId: userId,
        faberToken: faberToken,
        oldICCID: esimICCID
      })
  }

  return (
    <Dialog
      open={requestEsimDialog}
      onClose={() => {
        handleRequestEsimDialogClose()
      }}
      fullWidth={true}
      maxWidth='md'
      PaperComponent={PaperComponent}
      aria-labelledby='draggable-dialog-title'
    >
      <DialogTitle>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            mb: '-15px',
            mt: '-5px'
          }}
        >
          <CrossDialogBtn dialogClose={handleRequestEsimDialogClose} />
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-evenly'
          }}
        >
          <Stack
            sx={{
              alignItems: 'center',
              maxWidth: '400px'
            }}
          >
            <StyledIcon>
              <ChangeCircleIcon
                sx={{
                  fontSize: '3em',
                  color: circlesOrbitColorPalette.primary.dark
                }}
              />
            </StyledIcon>
            <NormalText
              isBold
              variant='h6'
              sx={{ borderBottom: '1px solid grey' }}
            >
              Replace eSIM
            </NormalText>
            <Box sx={{ mt: 2 }}>
              <TextColorFontBody sx={{ fontSize: '16px' }}>
                This action will <b>TERMINATE</b> customer&apos;s current eSIM,
                allocate a <b>NEW</b> eSIM and move all assigned packs onto it.
              </TextColorFontBody>
            </Box>
            <Box
              component='form'
              sx={{
                rowGap: '1em',
                columnGap: '1em',
                gridTemplateColumns: 'repeat(2, 1fr)',
                my: '20px',
                height: '15vh'
              }}
              display='grid'
              noValidate
              autoComplete='off'
            >
              <DemoDatePicker label='Date' disabled={true} />

              <TextField
                id='Requested By'
                label='Requested By'
                defaultValue={userEmail}
                variant='standard'
                disabled
              />
            </Box>
            <LoadingButton
              loading={isReplaceEsimLoading || isReplaceExternalEsimLoading}
              loadingIndicator={<CircularProgress size={20} />}
              variant='contained'
              onClick={() => handleConfirmRequest('replace')}
              sx={{
                '&:disabled': {
                  border: 'none'
                }
              }}
            >
              Replace eSIM
            </LoadingButton>
          </Stack>

          <>
            <Divider variant='middle' orientation='vertical' flexItem />
            <Stack
              sx={{
                alignItems: 'center',
                maxWidth: '400px'
              }}
            >
              <StyledIcon>
                <SendIcon
                  sx={{
                    fontSize: '3em',
                    color: circlesOrbitColorPalette.primary.dark
                  }}
                />
              </StyledIcon>
              <NormalText
                isBold
                textAlign='center'
                variant='h6'
                sx={{ borderBottom: '1px solid grey' }}
              >
                Resend eSIM activation email
              </NormalText>
              <Box sx={{ mt: 2 }}>
                <TextColorFontBody sx={{ fontSize: '16px' }}>
                  This action will only resend the eSIM activation email for
                  customer&apos;s <b>EXISTING</b> eSIM. It will <b> NOT </b>
                  replace the current eSIM.
                </TextColorFontBody>
              </Box>
              <Box sx={{ my: 2, height: '18vh' }} />
              <LoadingButton
                loading={isEmailSendingLoading}
                loadingIndicator={<CircularProgress size={20} />}
                variant='contained'
                onClick={() => handleConfirmRequest('resend')}
              >
                Resend email
              </LoadingButton>
            </Stack>
          </>
        </Box>
      </DialogContent>
    </Dialog>
  )
}

export default EsimRequestV3Dialog
interface RequestDialogProps {
  handleRequestEsimDialogClose: () => void
  requestEsimDialog: boolean
  userId: string
  esimICCID: string
  faberToken: string
  isExternal: boolean
}
